import {
  DsHeaderTagConfiguration,
  DsHeaderUserInfo,
  DsNavigationItem,
  DsNavigationBarModule,
  DsHeaderModule,
  DsBoxModule,
  DsButtonModule,
  DsIconModule,
} from '@bmw-ds/components';
import { Component, WritableSignal, signal, OnInit } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { CommonService } from './services/common.service';
import { JwksValidationHandler, OAuthService } from 'angular-oauth2-oidc';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { authCodeFlowConfigForB2E } from './services/sso.b2e.config';
import { authCodeFlowConfigForB2D } from './services/sso.b2d.config';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs/internal/operators/filter';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    DsNavigationBarModule,
    DsHeaderModule,
    DsBoxModule,
    DsButtonModule,
    DsIconModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  routerLink: string = '';
  isUserLoggedIn: boolean = false;
  isAuthenticating = signal(true);
  appType: any;
  username: any;
  userInfo: any;
  apiBaseUrl: string;
  constructor(
    private commonService: CommonService,
    private oauth: OAuthService,
    private router: Router,
    private http: HttpClient
  ) {
    this.apiBaseUrl = environment.isB2D
      ? environment.b2d.serverURL
      : environment.b2e.serverURL;
  }
  currentDate: string = '';
  currentTime: string = '';
  private isRedirectingToLogin = false;
  isUnauthorized = signal(false);
  ngOnInit(): void {
    //  this.apiBaseUrl = environment.isB2D ? environment.b2d.serverURL :environment.b2e.serverURL;
    this.initializelogin();

    this.currentDate = new Date().toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    this.currentTime = new Date().toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
    });
    this.commonService.setCurrentDate.next(this.currentDate);
    this.commonService.setCurrentTime.next(this.currentTime);
    this.routerLink = window.location.href;
    console.log('Current URL:', this.routerLink);
    this.router.events.pipe(
    filter(event => event instanceof NavigationStart)
  ).subscribe((event) => {
    const url = (event as NavigationStart).url.split('?')[0];
    // Only clear the saved serial when the user explicitly opens the book screen.
    // Drill-down screens must keep it so the field can be restored there.
    if (/\/campaign_inventory_internal(\/)?$/.test(url)) {
      this.commonService.isNavIconClicked.next(true);
      return;
    }
    // When navigating back to InventoryCampaignDetails, preserve ALL state so
    // the form can restore dropdown values, savedFormState, dealerCode etc.
    if (/\/InventoryCampaignDetails(\/)?$/.test(url)) {
      return;
    }
  });
  }

  // Dynamically set environment label based on environment config
  environmentTagConfig: WritableSignal<Partial<DsHeaderTagConfiguration>> =
    signal({
      label: environment.isB2E ? environment.b2e.env : environment.b2d.env,
    });

  //Navigation configuration
  navigationItems: WritableSignal<DsNavigationItem[]> = signal([
    {
      label: 'Inventory Campaign Details',
      routerLink: '/InventoryCampaignDetails',
      icon: 'home',
      target: '_blank',
    },
    ...(environment.isB2D
      ? []
      : [
          {
            label: 'National',
            routerLink: '/campaign_inventory_internal',
            icon: 'book',
            target: '_blank',
          },
        ]),
  ]);

  titleB2E = 'Inventory Campaign Details (Internal)-National';
  titleB2D = 'Inventory Campaign Details (Internal View)';
  title = 'ROSS-Inventory Campaign';

  breadcrumbItems = [
    { label: 'Home', href: '#' },
    { label: 'Inventory Campaign Details', href: '#' },
    { label: 'Campaign Details', href: '#' },
  ];

  async initializelogin() {
    console.log('OIDC started');
    this.oauth.configure(
      environment.isB2E ? authCodeFlowConfigForB2E : authCodeFlowConfigForB2D
    );
    this.oauth.tokenValidationHandler = new JwksValidationHandler();

    this.oauth.events
      .pipe(filter(e => ['session_expired', 'session_terminated'].includes(e.type)))
      .subscribe(e => {
        console.warn('OAuth session event:', e.type);
        console.info('OAuth session event received; initiating relogin flow.', {
          eventType: e.type,
          currentUrl: window.location.href,
        });
        this.triggerLogin();
      });

    try {
      // Get the full current path including any /icd prefix
      const fullPath = window.location.pathname;
      const hasOAuthCallback =
        window.location.search.includes('code=') ||
        window.location.search.includes('state=');

      console.log('Current path on init:', fullPath);
      console.log('Has OAuth callback:', hasOAuthCallback);

      // Convert full path to Angular route (remove /icd prefix if present)
      let angularRoute = fullPath;
      if (fullPath.startsWith('/icd/')) {
        angularRoute = fullPath.substring(4); // Remove '/icd' prefix
      } else if (fullPath === '/icd') {
        angularRoute = '/InventoryCampaignDetails';
      }

      // Store the intended path BEFORE OAuth redirect if not already stored and not coming back from OAuth
      if (
        !hasOAuthCallback &&
        angularRoute !== '/' &&
        angularRoute !== '/InventoryCampaignDetails'
      ) {
        const existingPath = sessionStorage.getItem('intended_path');
        if (!existingPath) {
          sessionStorage.setItem('intended_path', angularRoute);
          console.log('Stored intended Angular route:', angularRoute);
        }
      }

      // Load discovery document and handle OAuth callback in one step
      await this.oauth.loadDiscoveryDocumentAndTryLogin();

      // Check authentication status
      if (this.oauth.hasValidIdToken()) {
        console.log('Valid ID token detected after login bootstrap.');
        this.oauth.setupAutomaticSilentRefresh();
        // User is authenticated
        this.isUserLoggedIn = true;
        const claims: any = this.oauth.getIdentityClaims();

        if (claims) {
          this.getUserInfo();

          // Determine navigation path based on stored or current path
          let navigationPath = '/InventoryCampaignDetails';

          // Check if there's a stored intended path (from new tab or before OAuth redirect)
          const intendedPath = sessionStorage.getItem('intended_path');
          if (intendedPath) {
            navigationPath = intendedPath;
            console.log('Navigating to stored intended path:', navigationPath);
            // Clear the stored path after use
            sessionStorage.removeItem('intended_path');
          } else if (
            !hasOAuthCallback &&
            angularRoute &&
            angularRoute !== '/' &&
            angularRoute !== '/InventoryCampaignDetails'
          ) {
            // Not coming from OAuth and on a specific route - stay there
            navigationPath = angularRoute;
            console.log('Staying on current path:', navigationPath);
          }

          // Navigate to the determined route
          setTimeout(() => {
            this.router
              .navigate([navigationPath], {
                replaceUrl: true,
              })
              .then(() => {
                console.log('Successfully navigated to', navigationPath);
                this.isAuthenticating.set(false);
              });
          }, 100);
        }
      } else {
        // No valid token - store current path and initiate OAuth flow
        console.warn('No valid ID token found during startup; starting login flow.', {
          angularRoute,
          hasOAuthCallback,
        });
        if (
          angularRoute !== '/' &&
          angularRoute !== '/InventoryCampaignDetails'
        ) {
          sessionStorage.setItem('intended_path', angularRoute);
          console.log('Storing path before OAuth redirect:', angularRoute);
        }
        console.log('No valid token, redirecting to auth provider');
        // this.oauth.initCodeFlow();
	      this.triggerLogin();
        this.oauth.initLoginFlow();
      }
    } catch (error) {
      // console.error('Authentication error:', error);
      this.isUnauthorized.set(true);
      this.isAuthenticating.set(false);
    }
  }

  private triggerLogin(): void {
    if (this.isRedirectingToLogin) {
      console.info('Login redirect already in progress; skipping duplicate trigger.');
      return;
    }
    // Prevent a redirect loop: if we already redirected to IDP within the last 30 seconds
    // and still can't authenticate, show the unauthorized page instead of looping.
    const lastRedirect = parseInt(sessionStorage.getItem('oidc_last_redirect') || '0', 10);
    if (Date.now() - lastRedirect < 30000) {
      console.warn('Login trigger suppressed to avoid redirect loop.', {
        lastRedirect,
        now: Date.now(),
      });
      this.isUnauthorized.set(true);
      this.isAuthenticating.set(false);
      return;
    }
    this.isRedirectingToLogin = true;
    sessionStorage.setItem('oidc_last_redirect', String(Date.now()));
    console.warn('Redirecting to login due to expired or invalid session.', {
      currentUrl: window.location.href,
    });
    this.oauth.initCodeFlow();
  }

  private forceTriggerLoginForTest(): void {
    this.isRedirectingToLogin = false;
    sessionStorage.removeItem('oidc_last_redirect');
    this.triggerLogin();
  }

  getUserInfo(): any {
    const httpHeaders = new HttpHeaders({
      Authorization: 'Bearer ' + this.oauth.getAccessToken(),
    });
    this.http
      .get(this.oauth.userinfoEndpoint ? this.oauth.userinfoEndpoint : '', {
        headers: httpHeaders,
      })
      .subscribe({
        next: (response: any) => {
          console.info('UserInfo request succeeded; session is still valid.');
          this.isRedirectingToLogin = false;
          sessionStorage.removeItem('oidc_last_redirect');
          this.userInfo = response as any;
          // console.log(this.userInfo,'userInfo');
          this.commonService.setUserInfo(this.userInfo);
          console.log(this.userInfo, 'userInfo');
          this.username =
            this.userInfo.given_name + ' ' + this.userInfo.family_name;
        },
         error: (err: any) => {
          if (err?.status === 503) {
            console.warn('UserInfo endpoint returned 503 — session expired, re-initiating login.');
            console.info('503 from userinfo endpoint; invoking relogin flow.', {
              userinfoEndpoint: this.oauth.userinfoEndpoint,
            });
            this.triggerLogin();
          } else {
            console.error('UserInfo error:', err);
          }
        },
      });
  }
}
